import { useModalStatusMessage } from '@/hooks';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { UPDATE_PUBLIC_DEVICE } from '@features/deviceConfigurationModal/graphql/mutations/updatePublicDevice';
import { GET_DEVICE_PUBLIC_TAB_DATA } from '@features/deviceConfigurationModal/graphql/queries/getDevicePublicTabData';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

type FormValues = {
  deviceId: string;
  public: boolean;
  visibleToClient: boolean;
};

export const useUpdateForm = (deviceId: string) => {
  const { _ } = useLingui();
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage();
  const {
    data: { device },
  } = useSuspenseQuery(GET_DEVICE_PUBLIC_TAB_DATA, {
    variables: {
      deviceId,
    },
  });

  const methods = useForm<FormValues>({
    defaultValues: {
      deviceId,
      public: device.publicDevice != null,
      visibleToClient: device.visibleToClient,
    },
  });

  const [mutate] = useMutation(UPDATE_PUBLIC_DEVICE, {
    // AMARU-606: visibleToClient alimenta el campo derivado `visibleToUser` (server-computed) de
    // las listas de acceso ("Acceso directo"/"Todos los accesos"). Apollo normaliza `Device.visibleToClient`
    // automáticamente, pero NO recalcula un campo derivado en otra entidad (DeviceUser/UserDeviceAccessEntry) —
    // sin este refetch, esas listas quedan con `visibleToUser` stale si ya estaban cacheadas.
    refetchQueries: ['DevicesAccess_GetDevicesAccessForUser', 'UserDeviceAccess_GetAllDeviceAccessForUser'],
    onCompleted({ updateDevice }) {
      openModalSuccessMessage(_(msg`Dispositivo actualizado correctamente`));
      methods.reset({
        deviceId,
        public: updateDevice.publicDevice != null,
        visibleToClient: updateDevice.visibleToClient,
      });
    },
    onError(error) {
      openModalErrorMessage(_(msg`Error al actualizar dispositivo: ${error.message}`));
    },
  });

  useEffect(() => {
    methods.reset({
      deviceId,
      public: device.publicDevice != null,
      visibleToClient: device.visibleToClient,
    });
  }, [methods]);

  const onSubmit = methods.handleSubmit((data) => {
    mutate({
      variables: data,
    });
  });

  return {
    methods,
    onSubmit,
    device,
  };
};
